import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import { CheckinsReport } from '@/types/api';
import { getValueColorIndicator, MaximumvalueIndicators } from '@/utils/charts';
import { dateToMonthDayFormat, dateToPeriodFormat, numberToPercentNumber } from '@/utils/format';
import SubmittedCheckinsReportComponent from './SubmittedCheckinsReport.component';

interface SubmittedCheckinsReportContainerProps {
  checkinsReport?: CheckinsReport;
}

const SubmittedCheckinsReportContainer = function ({
  checkinsReport,
}: SubmittedCheckinsReportContainerProps): JSX.Element {
  const theme = useTheme();

  const [labels, setLabels] = useState<string[]>([]);
  const [averageByWeek, setAverageByWeek] = useState<number[]>([]);

  const [totalAverage, setTotalAverage] = useState<number>(0);
  const [totalAverageColorIndicator, setTotalAverageColorIndicator] = useState<string>('');

  const [totalSubmitted, setTotalSubmitted] = useState<number>(0);
  const [totalSubmittedOnTime, setTotalSubmittedOnTime] = useState<number>(0);

  const [averageVariation, setAverageVariation] = useState<number>(0);
  const [averageVariationSinceDate, setAverageVariationSinceDate] = useState<string>('');

  useEffect(() => {
    if (checkinsReport) {
      const labelsToSet =
        checkinsReport.checkinsWeek?.map(
          (checkinWeek) =>
            `${dateToMonthDayFormat(checkinWeek.startDate)} - ${dateToMonthDayFormat(
              checkinWeek.endDate
            )}`
        ) || [];
      setLabels(labelsToSet);

      const averageByWeekToSet =
        checkinsReport.checkinsWeek?.map((checkinWeek) =>
          numberToPercentNumber(checkinWeek.checkinAvg)
        ) || [];
      setAverageByWeek(averageByWeekToSet);

      const totalAverageToSet = checkinsReport.avgSubmissionRate ?? 0;
      setTotalAverage(totalAverageToSet);

      const totalSubmittedToSet = checkinsReport.submittedCheckins ?? 0;
      setTotalSubmitted(totalSubmittedToSet);

      const totalSubmittedOnTimeToSet = checkinsReport.submittedOnTime ?? 0;
      setTotalSubmittedOnTime(totalSubmittedOnTimeToSet);

      const averageVariationToSet = checkinsReport.differenceAvgSubmissionRate;
      setAverageVariation(averageVariationToSet);

      const averageVariationSinceDateToSet = checkinsReport.startDate
        ? dateToPeriodFormat(checkinsReport.startDate)
        : '';
      setAverageVariationSinceDate(averageVariationSinceDateToSet);
    }
  }, [checkinsReport]);

  useEffect(() => {
    const totalAverageColorIndicatorToSet = getValueColorIndicator({
      value: numberToPercentNumber(totalAverage),
      maximumIndicator: MaximumvalueIndicators.PERCENT,
      theme,
    });

    setTotalAverageColorIndicator(totalAverageColorIndicatorToSet);
  }, [theme, totalAverage]);

  const isAverageVariationPositive = useMemo(
    () => !!averageVariation && averageVariation > 1,
    [averageVariation]
  );

  return (
    <SubmittedCheckinsReportComponent
      labels={labels}
      averageByWeek={averageByWeek}
      totalAverage={totalAverage}
      totalAverageColorIndicator={totalAverageColorIndicator}
      totalSubmitted={totalSubmitted}
      totalSubmittedOnTime={totalSubmittedOnTime}
      averageVariation={averageVariation}
      averageVariationSinceDate={averageVariationSinceDate}
      isAverageVariationPositive={isAverageVariationPositive}
    />
  );
};

export default SubmittedCheckinsReportContainer;
